import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep, isEqual } from "lodash";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  getFilePreSignedUrl,
  getHarvestedPlots,
} from "../../../../utils/api";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";
import {
  fetchTemperatureData,
  getTodayDate,
} from "./utils";
import {
  calculateDifferenceForDateRange,
  extractTableDataFromRawData,
  getAverageFromPercentile,
  getSortedFilteredDatesRange as computeSortedFilteredDatesRange,
  generateEachDayForecast as generateEachDayForecastCore,
} from "./spinachForecastCore";
import {
  buildGreenhouseForecastRows,
  MAX_ROW_TASKS_CONCURRENCY,
  mapWithConcurrency,
  prepareGreenhouseForecastContext,
} from "./spinachForecastSummaryBuilder";
import { useUser } from "../../../../context/UserContext";
import { isPositiveTrendCorrectionEnabled } from "../../../../utils/positiveTrendCorrection";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const MAX_PRESIGNED_CONCURRENCY = 21;
const MAX_BLOB_FETCH_CONCURRENCY = 40;
const KEY_IN_DATA = "leaf_lengths_cm";

const useSpinachDashboardForecast = ({
  currentOrg,
  currentSite,
  currentCrop,
  siteMetadata,
  greenhouseData,
  selectedGreenhouses,
  selectedGreenhousesSet,
  allRowGraphData,
  excelRawData,
  allPlotDataLoading,
  gddBaseTemperature,
  latLong,
  setSummaryForecastData,
  setGreenHouseTableLoading,
  setBuildProgress,
}) => {
  const { isAdmin } = useUser();
  // OF-129: gate the positive-trend display correction (non-admins on opted-in
  // sites only). Consumed when building the forecast so a `corrected` payload
  // is attached to each row for DISPLAY; raw fields stay untouched.
  const trendCorrectionEnabled = useMemo(
    () =>
      isPositiveTrendCorrectionEnabled({
        isAdmin,
        site: currentSite,
        cropName: currentCrop?.name,
      }),
    [isAdmin, currentSite, currentCrop],
  );

  const [prefetchedTempData, setPrefetchedTempData] = useState([]);
  const [prefetchLoading, setPrefetchLoading] = useState(false);
  const [prefetchScheduled, setPrefetchScheduled] = useState(false);
  const [summaryBuildScheduled, setSummaryBuildScheduled] = useState(false);

  const prefetchPromiseRef = useRef(null);
  const prefetchRangeRef = useRef(null);
  const prefetchTimerRef = useRef(null);
  const prefetchedTempDataRef = useRef([]);
  const tempSliceCacheRef = useRef(new Map());
  const tempSlicePromiseCacheRef = useRef(new Map());

  const preSignedCacheRef = useRef(new Map());
  const avgRowLevelCacheRef = useRef(new WeakMap());
  const sortedRangeCacheRef = useRef(new WeakMap());

  const buildRunIdRef = useRef(0);
  const buildDebounceRef = useRef(null);

  useEffect(() => {
    prefetchRangeRef.current = null;
    prefetchPromiseRef.current = null;
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }
    setPrefetchScheduled(false);
    setPrefetchedTempData([]);
  }, [latLong]);

  useEffect(() => {
    prefetchedTempDataRef.current = prefetchedTempData;
    tempSliceCacheRef.current.clear();
    tempSlicePromiseCacheRef.current.clear();
  }, [prefetchedTempData]);

  // Simple p-limit style limiter
  const makeLimiter = (max) => {
    let active = 0;
    const queue = [];
    const next = () => {
      active--;
      if (queue.length) queue.shift()();
    };
    return (fn) =>
      new Promise((resolve, reject) => {
        const run = () => {
          active++;
          Promise.resolve()
            .then(fn)
            .then((res) => {
              resolve(res);
              next();
            })
            .catch((err) => {
              reject(err);
              next();
            });
        };
        if (active < max) run();
        else queue.push(run);
      });
  };

  const presignedLimit = useMemo(
    () => makeLimiter(MAX_PRESIGNED_CONCURRENCY),
    [],
  );
  const azureLimit = useMemo(() => makeLimiter(MAX_BLOB_FETCH_CONCURRENCY), []);

  const fetchAverageFromPreSignedJson = useCallback(
    async (jsonURL, fallbackNumber) => {
      // cache hit?
      const cache = preSignedCacheRef.current;
      if (cache.has(jsonURL)) return cache.get(jsonURL);

      const p = presignedLimit(async () => {
        const preSignedRes = await getFilePreSignedUrl(
          currentOrg,
          currentSite.id,
          currentCrop.name,
          jsonURL,
          siteMetadata?.CLOUD_STORAGE_NAME || "azure",
          true,
          true,
        );

        const url = preSignedRes?.data?.data?.url;
        if (!url) return fallbackNumber;

        // fetch the blob (also limited)
        return azureLimit(async () => {
          const resp = await fetch(url);
          if (!resp.ok) return fallbackNumber;
          const jsonData = await resp.json();
          return jsonData?.average_length_cm ?? fallbackNumber;
        });
      }).catch(() => fallbackNumber);

      // store the in-flight promise so concurrent callers share it
      cache.set(jsonURL, p);
      return p;
    },
    [
      currentOrg,
      currentSite?.id,
      currentCrop?.name,
      siteMetadata?.CLOUD_STORAGE_NAME,
      presignedLimit,
      azureLimit,
    ],
  );

  const prefetchTempDataForRange = useCallback(
    async (start, end) => {
      if (!latLong || !start || !end) return;
      setPrefetchLoading(true);
      try {
        const promise = (async () => {
          const data = await fetchTemperatureData(
            latLong,
            start,
            end,
            siteMetadata,
          );
          if (Array.isArray(data)) {
            data.sort((a, b) => new Date(a.date) - new Date(b.date));
            setPrefetchedTempData(data);
            return data;
          }
          return null;
        })();
        prefetchPromiseRef.current = promise;
        await promise;
      } catch (error) {
        console.error("Prefetch large temp data error:", error);
      } finally {
        setPrefetchLoading(false);
      }
    },
    [latLong, siteMetadata],
  );

  const computeDynamicPrefetchRange = useCallback(() => {
    if (!latLong) return null;

    const selectedSet = selectedGreenhousesSet;
    let minDate = null;
    let maxDate = null;

    for (const [ghName, rowsObj] of Object.entries(allRowGraphData || {})) {
      if (selectedSet.size && !selectedSet.has(ghName)) continue;
      if (!rowsObj || typeof rowsObj !== "object") continue;

      for (const rowEntries of Object.values(rowsObj)) {
        if (!Array.isArray(rowEntries)) continue;
        for (const entry of rowEntries) {
          const raw = entry?.date;
          if (!raw) continue;
          const d = dayjs(raw);
          if (!d.isValid()) continue;
          if (!minDate || d.isBefore(minDate)) minDate = d;
          if (!maxDate || d.isAfter(maxDate)) maxDate = d;
        }
      }
    }

    if (!minDate || !maxDate) return null;

    return {
      start: minDate.format(BACKEND_DATE_FORMAT),
      end: maxDate.add(5, "day").format(BACKEND_DATE_FORMAT),
    };
  }, [allRowGraphData, latLong, selectedGreenhousesSet]);

  useEffect(() => {
    if (allPlotDataLoading) return;
    const range = computeDynamicPrefetchRange();
    if (!range) {
      if (prefetchTimerRef.current) {
        clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = null;
      }
      setPrefetchScheduled(false);
      return;
    }

    const prev = prefetchRangeRef.current;
    const toDay = (d) => dayjs(d, BACKEND_DATE_FORMAT);
    const next = prev
      ? {
          start: toDay(prev.start).isBefore(toDay(range.start))
            ? prev.start
            : range.start,
          end: toDay(prev.end).isAfter(toDay(range.end))
            ? prev.end
            : range.end,
        }
      : range;

    if (prev && prev.start === next.start && prev.end === next.end) {
      return;
    }

    if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
    setPrefetchScheduled(true);
    prefetchTimerRef.current = setTimeout(() => {
      setPrefetchScheduled(false);
      prefetchRangeRef.current = next;
      prefetchTempDataForRange(next.start, next.end);
    }, 150);

    return () => {
      if (prefetchTimerRef.current) {
        clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = null;
      }
      setPrefetchScheduled(false);
    };
  }, [allPlotDataLoading, computeDynamicPrefetchRange, prefetchTempDataForRange]);

  const getTempSliceOrFetch = useCallback(
    async (startDateStr, endDateStr) => {
      const cacheKey = `${startDateStr}|${endDateStr}`;
      const cachedSlice = tempSliceCacheRef.current.get(cacheKey);
      if (cachedSlice) return cachedSlice;

      const pendingSlice = tempSlicePromiseCacheRef.current.get(cacheKey);
      if (pendingSlice) return pendingSlice;

      const cacheIfHistorical = (fetched) => {
        if (!Array.isArray(fetched) || !fetched.length) return;
        const today = getTodayDate(latLong);
        const historicalEndMax = today
          .subtract(3, "day")
          .format(BACKEND_DATE_FORMAT);
        if (endDateStr <= historicalEndMax) {
          tempSliceCacheRef.current.set(cacheKey, fetched);
        }
      };

      const loadSlice = async () => {
        let baseData = prefetchedTempDataRef.current;

        if (!baseData.length && prefetchPromiseRef.current) {
          try {
            const fetched = await prefetchPromiseRef.current;
            if (Array.isArray(fetched) && fetched.length) {
              baseData = fetched;
            }
          } catch (error) {
            console.error("Prefetch temp data await failed:", error);
          }
        }

        if (!baseData.length) {
          const fetched = await fetchTemperatureData(
            latLong,
            startDateStr,
            endDateStr,
            siteMetadata,
          );
          cacheIfHistorical(fetched);
          return fetched;
        }

        const globalStart = baseData[0].date;
        const globalEnd = baseData[baseData.length - 1].date;

        if (startDateStr >= globalStart && endDateStr <= globalEnd) {
          const sliced = baseData.filter(
            (item) => item.date >= startDateStr && item.date <= endDateStr,
          );
          if (sliced.length) {
            tempSliceCacheRef.current.set(cacheKey, sliced);
            return sliced;
          }
        } else {
          const fetched = await fetchTemperatureData(
            latLong,
            startDateStr,
            endDateStr,
            siteMetadata,
          );
          cacheIfHistorical(fetched);
          return fetched;
        }

        const fetched = await fetchTemperatureData(
          latLong,
          startDateStr,
          endDateStr,
          siteMetadata,
        );
        cacheIfHistorical(fetched);
        return fetched;
      };

      const slicePromise = loadSlice().finally(() => {
        tempSlicePromiseCacheRef.current.delete(cacheKey);
      });
      tempSlicePromiseCacheRef.current.set(cacheKey, slicePromise);
      return slicePromise;
    },
    [latLong, siteMetadata],
  );

  // ------------------------------------------------------------------
  //  Data & logic helpers
  //  Pure helpers (filterByNameAndBlock, extractTableDataFromRawData,
  //  calculateDifferenceForDateRange, getAverageFromPercentile,
  //  getSortedFilteredDatesRange, generateEachDayForecast) now live in
  //  ./spinachForecastCore so they can be parity-tested against the backend.
  // ------------------------------------------------------------------
  // `calculateDifferenceForDateRange` is an imported (stable) function, so it
  // needs no useMemo — a direct alias keeps a stable reference across renders.
  const memoizedCalcDiffRange = calculateDifferenceForDateRange;

  const getFilePercentile80_95 = useCallback(
    async (ghName, plotName, date, lengths, rowLevelData) => {
      const fallback = () => getAverageFromPercentile(lengths, 80, 95);

      try {
        if (rowLevelData) {
          const foundItem = rowLevelData.find((r) => r.date === date);
          const existingData = foundItem?.data?.percentiles?.["80_95"];
          const average_length = existingData?.average_length_cm;
          if (
            (typeof average_length === "number" &&
              Number.isFinite(average_length)) ||
            (typeof average_length === "string" &&
              average_length.trim() !== "" &&
              /^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(average_length.trim()))
          ) {
            return Number(average_length);
          }
        }
        return 0;
        // const jsonURL = `${currentSite?.name}/${currentCrop.name}/${date}/${ghName}/${plotName}/percentile_(80, 95)_data.json`;
        // const avg = await fetchAverageFromPreSignedJson(jsonURL, fallback());
        // return typeof avg === "number" ? avg : fallback();
      } catch {
        return fallback();
      }
    },
    [currentSite?.name, currentCrop?.name, fetchAverageFromPreSignedJson],
  );

  const getAverageRowLevelData = useCallback(
    async (ghName, rowName, rowLevelData) => {
      const validEntries = rowLevelData.filter((item) => {
        const { data } = item;
        // Use VISIBLE-or-harvested measurements only: drop rows explicitly
        // flagged is_visible=false (and not harvested), regardless of whether the
        // plot-data API returned all rows (superuser) or a cached/merged set.
        // This keeps the dashboard, the Excel export, and the backend forecast
        // consistent (the backend uses the same visible-or-harvested series).
        if (item.is_visible === false && !item.is_harvested) return false;
        return data && Object.keys(data).length > 0;
      });

      const results = await mapWithConcurrency(
        validEntries,
        async (entry) => {
          const { date, data } = entry;
          const lengths = (data[KEY_IN_DATA] || []).map((x) => x[1]);
          const avgP80_95 = await getFilePercentile80_95(
            ghName,
            rowName,
            date,
            lengths,
            rowLevelData,
          );

          return {
            date: dayjs(date).format(UI_DATE_FORMAT),
            averageLength: data.average_length_cm,
            stdDev: data.std_dev_cm,
            averagePercentile80_95: avgP80_95,
            // percentile_25: percents[25],
            // percentile_90: percents[90],
            // percentile_95: percents[95],
          };
        },
        MAX_ROW_TASKS_CONCURRENCY,
      );

      return results
        .filter(Boolean)
        .sort((a, b) =>
          dayjs(a.date, UI_DATE_FORMAT).diff(dayjs(b.date, UI_DATE_FORMAT)),
        );
    },
    [getFilePercentile80_95],
  );

  const getAverageRowLevelDataCached = useCallback(
    async (ghName, rowName, rowLevelData) => {
      if (!Array.isArray(rowLevelData)) return [];
      const cache = avgRowLevelCacheRef.current;
      if (cache.has(rowLevelData)) {
        return cache.get(rowLevelData);
      }
      const result = await getAverageRowLevelData(
        ghName,
        rowName,
        rowLevelData,
      );
      cache.set(rowLevelData, result);
      return result;
    },
    [getAverageRowLevelData],
  );

  const getSortedFilteredDatesRange = useCallback(
    (selectedDates, averageData, excludeDates = [], endInclusive = true) => {
      if (averageData.length === 0 || selectedDates.length !== 2) return null;
      const cache = sortedRangeCacheRef.current;
      let map = cache.get(averageData);

      const startKey = dayjs(selectedDates[0]).format(UI_DATE_FORMAT);
      const endKey = dayjs(selectedDates[1]).format(UI_DATE_FORMAT);
      const excludeKey = excludeDates?.length
        ? [...excludeDates].sort().join("|")
        : "";
      const key = `${startKey}|${endKey}|${endInclusive ? 1 : 0}|${excludeKey}`;

      if (map?.has(key)) return map.get(key);

      // Pure compute lives in spinachForecastCore; the cache here is a
      // hook-local memoisation optimisation only.
      const result = computeSortedFilteredDatesRange(
        selectedDates,
        averageData,
        excludeDates,
        endInclusive,
      );

      if (!map) {
        map = new Map();
        cache.set(averageData, map);
      }
      map.set(key, result);

      return result;
    },
    // computeSortedFilteredDatesRange is a stable module-level import (not a
    // reactive value) -> []; matches this repo's react-hooks lint expectation.
    [],
  );

  const getForecastLeafSize = async (
    lastMeasurementDate,
    totalAverage,
    totalAveragePercentile80_95,
    gddResult,
    tempSliceFetcher = getTempSliceOrFetch,
  ) => {
    if (!lastMeasurementDate?.isValid())
      return ["N/A", "N/A", "N/A", "N/A", "N/A"];

    // Guard: with no thermal accumulation in the historical window (e.g. an
    // empty/failed weather slice => gddResult is 0/NaN), the GDD-scaled forecast
    // is undefined. Return N/A rather than a forecast — this also keeps the
    // frontend consistent with the backend (build_row_forecast returns None /
    // skips the row), so backend-vs-frontend verification stays apples-to-apples.
    if (!gddResult || gddResult <= 0)
      return ["N/A", "N/A", "N/A", "N/A", "N/A"];

    try {
      const start = lastMeasurementDate
        .add(1, "day")
        .format(BACKEND_DATE_FORMAT);
      const end = lastMeasurementDate.add(5, "day").format(BACKEND_DATE_FORMAT);

      const tempData5Days = await tempSliceFetcher(start, end);
      return generateEachDayForecastCore(
        tempData5Days,
        totalAverage,
        totalAveragePercentile80_95,
        gddResult,
        gddBaseTemperature,
      );
    } catch (error) {
      console.error("Error fetching 5-day forecast:", error);
      return ["N/A", "N/A", "N/A", "N/A", "N/A"];
    }
  };

  const createHarvestedMap = (harvestedData) => {
    const map = new Map();
    harvestedData?.greenhouses?.forEach((gh) => {
      gh.plots?.forEach((plot) => {
        const key = `${gh.id}-${plot.id}`;
        map.set(key, {
          harvestedDate: plot.harvested_date,
          totalYield: plot.total_yield,
          weight: plot.total_weight,
        });
      });
    });
    return map;
  };

  const fetchHarvestedData = async (fetchAll = false) => {
    const greenhouseIds = new Set();
    const plotIds = new Set();
    let greenhouseList;
    if (!fetchAll) {
      greenhouseList = greenhouseData.filter((gh) =>
        selectedGreenhousesSet.has(gh.name),
      );
    } else {
      greenhouseList = cloneDeep(greenhouseData);
    }

    for (const greenhouse of greenhouseList) {
      greenhouseIds.add(greenhouse.id);
      for (const row of greenhouse.plots) {
        plotIds.add(row.id);
      }
    }
    if (greenhouseIds.size === 0) return {};

    const resp = await getHarvestedPlots(
      currentOrg,
      currentSite.id,
      currentCrop.id,
      Array.from(greenhouseIds),
      Array.from(plotIds),
    );
    return resp.data.data;
  };

  // ------------------------------------------------------------------
  //  buildSummaryForecast
  // ------------------------------------------------------------------
  const buildSummaryForecast = async (
    excelRows,
    allRowGraph,
    limitGreenhouses = null,
  ) => {
    if (!excelRows || !allRowGraph) return;

    setGreenHouseTableLoading(true);

    // identify the current run; cancel stale updates
    const runId = ++buildRunIdRef.current;

    // filter greenhouses once for this run
    const greenhouseList = greenhouseData.filter(
      (gh) =>
        selectedGreenhousesSet.has(gh.name) &&
        (!limitGreenhouses || limitGreenhouses.includes(gh.name)),
    );

    // reset summary & progress for a clean progressive build
    // setSummaryForecastData({});
    setBuildProgress({ done: 0, total: greenhouseList.length });

    // fetch harvested only once
    const harvestedData = await fetchHarvestedData();
    const harvestedMap = createHarvestedMap(harvestedData);

    const type4Greenhouses = new Set();
    const greenhouseMap = new Map();

    // 1) Precompute rowDataMap per greenhouse
    for (const greenhouse of greenhouseList) {
      const ghName = greenhouse.name;
      const { rowDataMap, allRowsHarvested } =
        prepareGreenhouseForecastContext({
          greenhouse,
          excelRows,
          allRowGraph,
          harvestedMap,
          extractTableDataFromRawData,
        });

      if (allRowsHarvested) type4Greenhouses.add(ghName);
      greenhouseMap.set(ghName, { greenhouse, rowDataMap });
    }

    // 2) Keep your "type4 last" ordering
    const processingOrder = [
      ...greenhouseList.filter((gh) => !type4Greenhouses.has(gh.name)),
      ...greenhouseList.filter((gh) => type4Greenhouses.has(gh.name)),
    ];

    const filterRules =
      currentSite?.metadata?.[currentCrop?.name]
        ?.FORECAST_GENERIC_FILTER_RULES || [];

    // 3) Process concurrently; push each greenhouse as soon as it’s done
    await Promise.all(
      processingOrder.map(async (greenhouse) => {
        const ghName = greenhouse.name;
        const { rowDataMap } = greenhouseMap.get(ghName);
        let rowForecasts = [];

        try {
          rowForecasts = await buildGreenhouseForecastRows({
            greenhouse,
            rowDataMap,
            allRowGraph,
            filterRules,
            latLong,
            gddBaseTemperature,
            getAverageRowLevelData: getAverageRowLevelDataCached,
            getSortedFilteredDatesRange,
            memoizedCalcDiffRange,
            getTempSliceOrFetch,
            getForecastLeafSize,
            maxRowTasksConcurrency: MAX_ROW_TASKS_CONCURRENCY,
            correctNegativeTrend: trendCorrectionEnabled,
          });
        } catch (ghError) {
          console.error(
            `Greenhouse-level error for greenhouse=${ghName}:`,
            ghError,
          );
        }

        // Ignore if a newer run started
        if (buildRunIdRef.current !== runId) return;

        // Progressive append (one greenhouse at a time)
        setSummaryForecastData((prev) => {
          const prevRows = prev[ghName];
          if (prevRows && isEqual(prevRows, rowForecasts)) return prev;
          return { ...prev, [ghName]: rowForecasts };
        });

        // Monotonic progress + guard
        setBuildProgress((prev) => {
          if (buildRunIdRef.current !== runId) return prev;
          const nextDone = Math.min(prev.done + 1, prev.total);
          return { ...prev, done: nextDone };
        });
      }),
    );

    // Ignore completion if a newer run started
    if (buildRunIdRef.current !== runId) return;
    if (!limitGreenhouses || limitGreenhouses.length === 0) {
      setSummaryForecastData((prev) => {
        const keep = new Set(
          greenhouseData
            .filter((gh) => selectedGreenhousesSet.has(gh.name))
            .map((g) => g.name),
        );
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (!keep.has(k)) delete next[k];
        });
        return next;
      });
    }

    setGreenHouseTableLoading(false);
  };

  // once excelRawData & allRowGraphData => build summary
  useEffect(() => {
    const ready =
      greenhouseData &&
      excelRawData &&
      selectedGreenhouses.length > 0 &&
      !allPlotDataLoading; // wait until graph fetching is not actively running

    if (!ready) {
      if (summaryBuildScheduled) setSummaryBuildScheduled(false);
      return;
    }

    if (buildDebounceRef.current) clearTimeout(buildDebounceRef.current);
    setSummaryBuildScheduled(true);
    buildDebounceRef.current = setTimeout(() => {
      // single entry point to rebuild
      setSummaryBuildScheduled(false);
      buildSummaryForecast(excelRawData, allRowGraphData);
    }, 300);

    return () => {
      if (buildDebounceRef.current) {
        clearTimeout(buildDebounceRef.current);
        buildDebounceRef.current = null;
      }
      setSummaryBuildScheduled(false);
    };
  }, [
    excelRawData,
    allRowGraphData,
    greenhouseData,
    selectedGreenhouses,
    allPlotDataLoading,
    // Rebuild if the correction gate flips (e.g. a late-arriving admin profile),
    // otherwise the forecast keeps a stale correction (OF-129: admins see raw).
    trendCorrectionEnabled,
  ]);

  return {
    prefetchLoading,
    prefetchScheduled,
    summaryBuildScheduled,
    buildSummaryForecast,
    fetchHarvestedData,
    createHarvestedMap,
    extractTableDataFromRawData,
    getAverageRowLevelData,
    getSortedFilteredDatesRange,
    memoizedCalcDiffRange,
    getTempSliceOrFetch,
    getForecastLeafSize,
  };
};

export default useSpinachDashboardForecast;
